import React from 'react';
import {createRoot} from 'react-dom/client';
import App from './App';
import {persistor, store} from './redux';
import {Provider} from 'react-redux';
import {PersistGate} from "redux-persist/integration/react";
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import PageLoading from "./components/core/PageLoading";
import ErrorFallback from "./components/core/ErrorFallback";
import {ErrorBoundary} from '@sentry/react';
import {initMonitoring} from './services/monitoring';
import {applyCachedBrandTokens} from './utils/branding';

initMonitoring();

// Apply this host's cached tenant branding synchronously, before React mounts,
// so returning storefront visitors never see the default-theme colour flash.
// No-op on hosts with no cached branding (e.g. the authenticated app domain).
applyCachedBrandTokens();

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(
  <React.StrictMode>
      <ErrorBoundary fallback={<ErrorFallback/>}>
          <Provider store={store}>
              <PersistGate loading={ <PageLoading showLoading={false}/>} persistor={persistor}>
                  <App/>
              </PersistGate>
          </Provider>
      </ErrorBoundary>
  </React.StrictMode>
);

// Auto-activate deployed updates: when a new service worker is installed (or
// found already waiting), tell it to skip waiting, then reload once it takes
// control so the user gets the new version immediately instead of after
// closing every tab. The flag restricts the reload to updates we requested,
// so the first-ever install (controllerchange via clients.claim) and
// unrelated controller changes never trigger it.
let swUpdateRequested = false;
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    swUpdateRequested = true;
    registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
  },
});

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!swUpdateRequested) return;
    swUpdateRequested = false;
    window.location.reload();
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
